import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";

export function HoldToConfirm({
  label,
  onConfirm,
  className,
  duration = 1600,
  intent = "default",
}: {
  // ReactNode (not just string) so a call site can pair a leading icon with
  // the label (e.g. the alarm Clear's ✕) without losing the at-a-glance glyph
  // when a plain <button> becomes a hold gate. Existing string callers are
  // unaffected — a string is a valid ReactNode.
  label: ReactNode;
  onConfirm: () => void;
  className?: string;
  duration?: number;
  intent?: "default" | "destructive" | "warn";
}) {
  const t = useT();
  const [holding, setHolding] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);
  const timer = useRef<number | null>(null);
  // G25: under prefers-reduced-motion the progress bar is frozen by the global
  // reduced-motion rule, so those users (and screen readers) get no progress
  // cue. Give them a discrete 1 s textual countdown. Read once — the app
  // treats reduced-motion statically, so a live listener is unnecessary.
  const reduced = useRef<boolean>(
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  ).current;
  const [remaining, setRemaining] = useState<number | null>(null);
  const tick = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    if (tick.current) window.clearInterval(tick.current);
  }, []);
  function start() {
    if (timer.current !== null) return; // already holding (mixed pointer+key input)
    setHolding(true);
    if (reduced) {
      setRemaining(Math.ceil(duration / 1000));
      tick.current = window.setInterval(
        () => setRemaining((r) => (r != null && r > 1 ? r - 1 : r)),
        1000,
      );
    }
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (tick.current) { window.clearInterval(tick.current); tick.current = null; }
      setRemaining(null);
      onConfirm();
      setHolding(false);
    }, duration);
  }
  function cancel() {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (tick.current) { window.clearInterval(tick.current); tick.current = null; }
    setRemaining(null);
    setHolding(false);
  }
  // Keyboard hold (XTRA-03): Space/Enter keydown starts the same timer the
  // pointer path uses; keyup or focus loss cancels — so a keyboard-only
  // operator can save a schedule / apply an override / delete a rule.
  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== " " && e.key !== "Enter") return;
    // Suppress the default button activation (Enter clicks on keydown,
    // Space on keyup) — the completed hold is the only confirm path.
    e.preventDefault();
    if (e.repeat) return; // OS key-repeat must not restart the timer
    start();
  }
  function onKeyUp(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === " " || e.key === "Enter") cancel();
  }
  const intentClass = {
    default: "border-accent/30 bg-accent/10 text-accent hover:bg-accent/15",
    warn: "border-warn/40 bg-warn/15 text-warn hover:bg-warn/25",
    destructive: "border-critical/40 bg-critical/15 text-critical hover:bg-critical/25",
  }[intent];
  const barClass = {
    default: "bg-accent",
    warn: "bg-warn",
    destructive: "bg-critical",
  }[intent];
  return (
    <button
      ref={ref}
      onMouseDown={start}
      onTouchStart={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onTouchEnd={cancel}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={cancel}
      aria-busy={holding}
      aria-description={t("manage.hold.aria", { seconds: duration / 1000 })}
      className={cn(
        "relative flex h-10 select-none items-center justify-center gap-2 overflow-hidden rounded-md border px-4 text-xs font-semibold uppercase tracking-wider transition",
        intentClass,
        holding && "holding",
        className,
      )}
      style={{ ['--hold-duration' as any]: `${duration}ms` }}
    >
      <span className="absolute inset-y-0 left-0 hold-bar w-full opacity-30" style={{ transitionDuration: `${duration}ms` }}>
        <span className={cn("block h-full", barClass)} />
      </span>
      <span className="relative">{holding ? (reduced && remaining != null ? t("manage.hold.holding", { seconds: remaining }) : t("manage.hold.confirming")) : label}</span>
    </button>
  );
}
